import { ReactComponent as BriefcaseIcon } from "@assets/icons/briefcase.svg";
import { ReactComponent as CheckIcon } from "@assets/icons/checked.svg";
import { ReactComponent as DocumentIcon } from "@assets/icons/document.svg";
import { ReactComponent as PennyIcon } from "@assets/icons/penny_icon.svg";
import { ReactComponent as PersonIcon } from "@assets/icons/person.svg";
import ModalHandyPA from "@components/Modals/ModalHandyPA";
import HeaderProgress from "@components/Progress/HeaderProgress";
import Loading from "@components/layout/Loading";
import { useGetUser } from "@utils/hooks";
import PensionCard from "@view/pension_application/PensionCard";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

type PensionApplicationProps = {};

const PensionApplication: React.FC<PensionApplicationProps> = () => {
  const navigate = useNavigate();
  const { data: userData, isLoading: userLoading } = useGetUser();
  const [openModalHandyPA, setOpenModalHandyPA] = useState(false);

  const processOverviewSteps = useMemo(() => {
    let obj = [
      {
        label: "Activity Information",
        time: "1 min",
        icon: <BriefcaseIcon />,
        completed: userData?.pension_applied_activity_information,
        path: "activity-details",
      },
      {
        label: "Verify Identity",
        time: "1 min",
        icon: <PersonIcon width={24} height={24} />,
        completed: userData?.pension_applied_verify_identity,
        path: "verify-identity",
      },
      {
        label:
          userData?.employment_status === "Company Director" ||
          userData?.employment_status === "Self-Employed/Sole Trader"
            ? "Review PRSA Form"
            : "Review PRSA Form",
        time: "4 min",
        icon: <DocumentIcon />,
        completed:
          userData?.pension_applied_prsa_form ??
          userData?.process_final_pension_application,
        path: "prsa-form",
      },
    ];

    if (
      userData?.employment_status === "Not currently working" ||
      userData?.employment_status === "Employee"
    ) {
      obj.shift();
    }

    const pathTo = !obj.every((i) => !!i.completed)
      ? obj.filter((i) => !i.completed)?.[0].path
      : "/";
    const next = !obj.every((i) => !!i.completed)
      ? obj.filter((i) => !i.completed)[0].label
      : "";

    const labelNext = {
      "activity-details": "Add Activity Information",
      "verify-identity": "Verify Identity",
      "prsa-form": "Review Form",
      "/": "Complete Application",
    };

    return {
      steps: obj,
      path: pathTo,
      next,
      labelNext: labelNext[pathTo as keyof typeof labelNext],
    };
  }, [userData]);

  if (userLoading) return <Loading />;

  return (
    <>
      <ModalHandyPA
        onClose={() => {
          setOpenModalHandyPA(false);
        }}
        open={openModalHandyPA}
      />

      <section className="grow flex items-start md:items-center justify-center border-t-4 border-l-0 md:border-t-0 md:border-l-4 border-[#F4E34E] px-0 md:px-2 lg:px-0">
        <div className="max-w-full md:max-w-[880px] w-full md:rounded-3xl md:border md:border-pn_gray-200">
          <HeaderProgress
            currentStep={Number(
              processOverviewSteps.steps.filter((i) => !!i.completed).length
            )}
            totalSteps={Number(processOverviewSteps.steps.length)}
            splited={Number(processOverviewSteps.steps.length)}
            closeButtonEvent={() => navigate("/")}
          />

          <div className="flex flex-col md:flex-row md:items-stretch px-4 md:px-0 pb-2 md:pb-0">
            <div className="flex-1 py-4 pr-0 pl-0 md:py-6 md:pr-6 md:pl-8">
              <div className="p-0 md:p-4 flex flex-col gap-y-6">
                <div className="bg-[#F4E34E] rounded-full w-[60px] h-[60px] flex items-center justify-center">
                  <PennyIcon className="w-[24px] h-[27px]" />
                </div>

                <div className="flex flex-col gap-y-2">
                  <span className="text-pn-large-title text-pn_gray-950">
                    Complete your application online and start saving towards
                    your pension
                  </span>
                  <span className="text-pn-body-large-regular text-pn_gray-600">
                    The application will take you about{" "}
                    <span className="text-pn-body-large-emphasis text-pn_gray-950">
                      6 minutes
                    </span>{" "}
                    to complete as you've already answered most of the
                    questions. You can always save and come back if you need to.
                  </span>
                </div>
              </div>
            </div>

            <div className="flex-1 pb-4 pl-0 pr-0 md:py-6 md:pl-6 md:pr-8 flex flex-col gap-y-4 md:gap-y-2">
              <div className="flex flex-col">
                <span className="text-pn-body-large-emphasis text-pn_gray-950 py-2">
                  Process Overview
                </span>
                <PensionCard
                  pensionApplied={
                    userData?.pension_applied ||
                    userData?.process_pension_applied
                  }
                  contribution={userData?.pension_contribution_value || 0}
                />
              </div>

              <div className="flex flex-col gap-y-2">
                <div className="pt-4 md:pt-2 md:pb-2">
                  <span className="text-pn-body-large-emphasis text-pn_gray-950">
                  Process Overview
                  </span>
                </div>

                {processOverviewSteps.steps.map((item, index) => (
                  <div className="px-4 py-6 rounded-2xl border border-pn_gray-200 bg-pn_white flex items-start gap-x-4">
                    <div
                      className={twMerge(
                        item.completed
                          ? "bg-pn_green-500"
                          : processOverviewSteps.next === item.label
                          ? "bg-[#F4E34E]"
                          : "bg-pn_gray-200",
                        "rounded-full w-6 h-6 flex items-center justify-center text-pn-button-2"
                      )}
                    >
                      {item.completed ? (
                        <CheckIcon className="w-4 h-4" />
                      ) : (
                        index + 1
                      )}
                    </div>

                    <div className="flex-1 flex flex-col gap-y-2">
                      <div className="flex items-center space-x-2">
                        <span className="text-pn-body-emphasis flex-1 text-pn_black">
                          {item.label}
                        </span>
                        <span className="text-pn-callout-regular">
                          {item.completed ? "Completed" : item.time}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-[17px] md:mt-0 flex flex-col md:flex-row md:items-center md:gap-x-2 gap-y-4 pb-[46.5px] md:pb-6 px-4 md:px-8 md:pt-2">
            <span className="hidden md:block text-pn-callout-regular text-pn_gray-600 flex-1">
              {processOverviewSteps.path === "/"
                ? ""
                : "Your information is always secure with Penny"}
            </span>
            {processOverviewSteps.path === "activity-details" ? (
              <button
                onClick={() => setOpenModalHandyPA(true)}
                className="btn-primary-lg md:btn-primary-sm2 text-center"
              >
                {processOverviewSteps.labelNext}
              </button>
            ) : (
              <Link
                to={processOverviewSteps.path}
                className="btn-primary-lg md:btn-primary-sm2 text-center"
              >
                {processOverviewSteps.labelNext}
              </Link>
            )}
            <span className="block md:hidden text-center text-pn-callout-regular">
              {processOverviewSteps.path === "/"
                ? ""
                : "Your information is always secure with Penny"}
            </span>
          </div>
        </div>
      </section>
    </>
  );
};
export default PensionApplication;
